import React, { useEffect, useMemo, useState } from 'react';
import { fetchWidgetContent, type WidgetManifest } from '../../services/myToolsApi';
import { scanWidgetHtml, summarizeScan, type ScanFinding } from './widgetScan';
import '../../styles/Widgets.css';

export interface WidgetScanReportProps {
  /** Flattened document to scan (pre-save preview). Takes precedence over toolId. */
  html?: string | null;
  /** Installed/reviewable widget — the content is fetched with the viewer's own access. */
  toolId?: string;
  manifest?: WidgetManifest | null;
  /** One-line summary with an expandable list (marketplace detail, wizard). */
  compact?: boolean;
  className?: string;
}

const SEVERITY_LABEL: Record<ScanFinding['severity'], string> = { high: 'Review', warn: 'Check', info: 'Note' };

/** Static code scan of what actually runs in the sandbox — a reading guide for reviewers, not a verdict. */
const WidgetScanReport: React.FC<WidgetScanReportProps> = ({ html, toolId, manifest, compact = false, className }) => {
  const [fetched, setFetched] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(!compact);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (html != null || !toolId) return;
    const controller = new AbortController();
    setFetched(null);
    setError(null);
    fetchWidgetContent(toolId, controller.signal)
      .then(doc => { if (!controller.signal.aborted) setFetched(doc); })
      .catch(err => { if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Could not load the widget code'); });
    return () => controller.abort();
  }, [html, toolId]);

  const source = html ?? fetched;
  const findings = useMemo(() => (source ? scanWidgetHtml(source, manifest) : []), [source, manifest]);
  const summary = useMemo(() => summarizeScan(findings), [findings]);

  if (error) return <p className={`wg-scan__empty${className ? ` ${className}` : ''}`}>Code scan unavailable: {error}</p>;
  if (source == null) return <p className={`wg-scan__empty${className ? ` ${className}` : ''}`}>Scanning widget code…</p>;

  const headline = summary.total === 0
    ? 'No flags — nothing unusual found in the code'
    : [summary.high && `${summary.high} to review`, summary.warn && `${summary.warn} to check`, summary.info && `${summary.info} note${summary.info === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
  const tone = summary.high ? 'high' : summary.warn ? 'warn' : summary.total ? 'info' : 'clean';

  return (
    <div className={`wg-scan${className ? ` ${className}` : ''}`}>
      <button type="button" className={`wg-scan__head wg-scan__head--${tone}`} onClick={() => setOpen(o => !o)} aria-expanded={open} disabled={summary.total === 0 && compact}>
        <span className="wg-scan__dot" aria-hidden="true" />
        <span className="wg-scan__title">Code scan</span>
        <span className="wg-scan__headline">{headline}</span>
        {summary.total > 0 && <span className="wg-scan__chevron">{open ? '▾' : '▸'}</span>}
      </button>
      {open && findings.length > 0 && (
        <ul className="wg-scan__list">
          {findings.map(f => (
            <li key={f.code} className={`wg-scan__item wg-scan__item--${f.severity}`}>
              <div className="wg-scan__row">
                <span className={`wg-scan__sev wg-scan__sev--${f.severity}`}>{SEVERITY_LABEL[f.severity]}</span>
                <span className="wg-scan__label">{f.title}</span>
                <span className="wg-scan__count">×{f.count}</span>
                {f.samples.length > 0 && (
                  <button type="button" className="wg-scan__more" onClick={() => setExpanded(e => ({ ...e, [f.code]: !e[f.code] }))}>
                    {expanded[f.code] ? 'Hide' : 'Show'} where
                  </button>
                )}
              </div>
              <p className="wg-scan__detail">{f.detail}</p>
              {expanded[f.code] && (
                <div className="wg-scan__samples">
                  {f.samples.map((s, i) => <code key={i} className="wg-scan__sample">…{s}…</code>)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && findings.length > 0 && (
        <p className="wg-scan__foot">The sandbox and Content-Security-Policy still apply regardless of these flags — they tell you what to read, not what will run.</p>
      )}
    </div>
  );
};

export default React.memo(WidgetScanReport);
